import { useEffect, useState, useCallback } from 'react';

import { TUsePollingQueryParams } from '@/shared/hooks/use-polling-query/use-polling-query.types';

const DEFAULT_PERIODIC_INTERVAL = 2500; // 2.5 second in milliseconds

export const usePollingQuery = <T>({
  useQueryHook,
  queryArg,
  shouldStopPolling = () => false,
  queryOptions = {},
  enableLogging = false,
  pollingInterval = DEFAULT_PERIODIC_INTERVAL,
}: TUsePollingQueryParams<T>) => {
  const [pollingEnabled, setPollingEnabled] = useState<boolean>(true);

  const queryResult = useQueryHook(queryArg, {
    ...queryOptions,
    pollingInterval: pollingEnabled ? pollingInterval : 0,
  });

  const logPollingResults = useCallback(() => {
    if (enableLogging && queryResult?.data) {
      console.debug(`Polling result`, queryResult?.data);
    }
  }, [enableLogging, queryResult?.data]);

  const updatePollingStateBasedOnData = useCallback(() => {
    if (!queryResult?.data) {
      return;
    }

    const shouldStop = shouldStopPolling(queryResult?.data);
    setPollingEnabled(!shouldStop);
  }, [queryResult?.data, shouldStopPolling]);

  const handleErrorState = useCallback(() => {
    if (queryResult?.error) {
      setPollingEnabled(false);
    }
  }, [queryResult?.error]);

  useEffect(() => {
    logPollingResults();
  }, [logPollingResults]);

  useEffect(() => {
    updatePollingStateBasedOnData();
  }, [updatePollingStateBasedOnData]);

  useEffect(() => {
    handleErrorState();
  }, [handleErrorState]);

  return {
    ...queryResult,
    pollingEnabled,
    setPollingEnabled,
  };
};
